import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '@/hooks/useScrollReveal';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import HowItWorksSection from './components/HowItWorksSection';
import InspirationSection from './components/InspirationSection';
import EmotionalSection from './components/EmotionalSection';
import TrustLeadSection from './components/TrustLeadSection';
import WhatsAppCTASection from './components/WhatsAppCTASection';
import TestimonialsSection from './components/TestimonialsSection';
import FloatingWhatsApp from './components/FloatingWhatsApp';

const WHATSAPP_URL =
  'https://wa.me/905301714669?text=Merhaba%20SARA%20KUYUMCULUK%2C%20%C3%B6zel%20bir%20tasar%C4%B1m%20talep%20etmek%20istiyorum.';

function Footer() {
  return (
    <footer className="bg-luxury-black border-t border-luxury-border py-12 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/1dc01291-9518-472c-8814-2fd5a6290899.png"
              alt="Sara Kuyumculuk"
              className="h-8 w-auto object-contain mb-4"
            />
            <p className="text-white/40 text-sm font-sans leading-relaxed">
              Kişiye özel altın ve gümüş tasarımlar. Her parça, bir hikaye.
            </p>
          </div>
          <div>
            <h4 className="text-white text-sm font-sans mb-4 tracking-wide">
              Sayfalar
            </h4>
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                className="text-white/40 hover:text-gold text-sm transition-colors"
              >
                Ana Sayfa
              </Link>
              <Link
                to="/hakkimizda"
                className="text-white/40 hover:text-gold text-sm transition-colors"
              >
                Hakkımızda
              </Link>
              <Link
                to="/sss"
                className="text-white/40 hover:text-gold text-sm transition-colors"
              >
                Sıkça Sorulan Sorular
              </Link>
            </div>
          </div>
          <div>
            <h4 className="text-white text-sm font-sans mb-4 tracking-wide">
              İletişim
            </h4>
            <div className="flex flex-col gap-2">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="nofollow noreferrer"
                className="text-white/40 hover:text-gold text-sm transition-colors"
              >
                WhatsApp: +90 530 171 4669
              </a>
              <span className="text-white/40 text-sm">İzmir Konak, Türkiye</span>
            </div>
          </div>
          <div>
            <h4 className="text-white text-sm font-sans mb-4 tracking-wide">
              Sosyal Medya
            </h4>
            <div className="flex flex-col gap-2">
              <a
                href="https://www.instagram.com/sara.kuyumculuk"
                target="_blank"
                rel="nofollow noreferrer"
                className="text-white/40 hover:text-gold text-sm transition-colors inline-flex items-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-instagram-line" />
                </span>
                Instagram
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-luxury-border pt-6 text-center">
          <p className="text-white/30 text-xs font-sans">
            &copy; {new Date().getFullYear()} Sara Kuyumculuk. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  useScrollReveal();

  useEffect(() => {
    document.title = 'Sara Kuyumculuk – Kişiye Özel Altın ve Gümüş Tasarım';
  }, []);

  return (
    <main className="bg-luxury-black min-h-screen overflow-x-hidden">
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <HowItWorksSection />
      <InspirationSection />
      <EmotionalSection />
      <TrustLeadSection />
      <TestimonialsSection />
      <WhatsAppCTASection />

      {/* Visit Us / Google Maps */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-luxury-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-gold text-xs tracking-[0.3em] uppercase mb-4 font-sans">
                Atölyemiz
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-6">
                Bizi İzmir Konak&apos;ta Ziyaret Edin
              </h2>
              <p className="text-white/50 text-sm md:text-base leading-relaxed font-sans mb-8">
                Kişiye özel tasarımlarınızı birlikte konuşmak, örnek parçaları
                görmek ve ölçü almak için atölyemize bekleriz.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-gold/30 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-gold" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-sans">Adres</p>
                    <p className="text-white/50 text-sm font-sans">
                      İzmir Konak, Türkiye
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-gold/30 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-gold" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-sans">Çalışma Saatleri</p>
                    <p className="text-white/50 text-sm font-sans">
                      Pazartesi – Cumartesi: 09:00 – 19:00
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-gold/30 rounded-md flex-shrink-0 mt-0.5">
                    <i className="ri-whatsapp-line text-gold" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-sans">Randevu</p>
                    <p className="text-white/50 text-sm font-sans">
                      WhatsApp üzerinden önceden haber vermeniz yeterli
                    </p>
                  </div>
                </div>
              </div>
              <a
                href="https://www.google.com/maps?q=38.4212799,27.1330624"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 text-sm rounded-sm hover:bg-gold/10 transition-colors duration-300 whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-direction-line" />
                </span>
                Yol Tarifi Al
              </a>
            </div>
            <div className="rounded-lg overflow-hidden border border-luxury-border">
              <iframe
                src="https://maps.google.com/maps?q=38.4212799,27.1330624&z=15&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Sara Kuyumculuk Konum"
                className="grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Instagram CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-luxury-surface">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 border border-gold/30 rounded-full">
            <i className="ri-instagram-line text-gold text-2xl" />
          </div>
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-4 font-sans">
            @sara.kuyumculuk
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Bizi Instagram&apos;da Takip Edin
          </h2>
          <p className="text-white/50 text-base md:text-lg mb-10 font-sans max-w-xl mx-auto">
            Tamamlanan özel tasarımlarımızı, süreçlerimizi ve ilham veren
            parçalarımızı Instagram&apos;dan takip edebilirsiniz.
          </p>
          <a
            href="https://www.instagram.com/sara.kuyumculuk"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-3.5 text-sm rounded-sm hover:bg-gold/10 transition-colors duration-300 whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-instagram-line" />
            </span>
            Instagram&apos;da Gör
          </a>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
